/**
 * Local calendar days (`YYYY-MM-DD`, as an `<input type="date">` produces)
 * converted to and from UTC instants. Users pick days in the browser's time
 * zone; the API only ever receives ISO 8601 UTC instants.
 */

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDay(day: string): Date | null {
	const match = DAY_PATTERN.exec(day);
	if (!match) return null;

	const [, year, month, date] = match;
	const value = new Date(Number(year), Number(month) - 1, Number(date));
	return Number.isNaN(value.getTime()) ? null : value;
}

function pad(value: number): string {
	return String(value).padStart(2, '0');
}

/** `YYYY-MM-DD` of `date` in the browser's time zone. */
export function toLocalDay(date: Date): string {
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** 00:00 local of `day`, as an ISO UTC instant. */
export function localDayStartIso(day: string): string | undefined {
	return parseDay(day)?.toISOString();
}

/**
 * 00:00 local of the day after `day`, as an ISO UTC instant: an exclusive
 * upper bound that includes the whole selected day.
 */
export function localDayEndIso(day: string): string | undefined {
	const start = parseDay(day);
	if (!start) return undefined;
	start.setDate(start.getDate() + 1);
	return start.toISOString();
}

/** `day` shifted by `days` calendar days (negative goes back). */
export function addLocalDays(day: string, days: number): string {
	const date = parseDay(day);
	if (!date) return day;
	date.setDate(date.getDate() + days);
	return toLocalDay(date);
}

/**
 * First local day whose 00:00 is at or after `instant`. Used to disable the
 * days before a server-provided lower bound.
 */
export function firstLocalDayFrom(instant: string): string | undefined {
	const at = new Date(instant);
	if (Number.isNaN(at.getTime())) return undefined;

	const day = toLocalDay(at);
	const start = parseDay(day) as Date;
	return start.getTime() < at.getTime() ? addLocalDays(day, 1) : day;
}
